import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import SubHeader from '../../layout/components/SubHeader';
import dashboardService from '../../../services/dashboard.service';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import DashboardSection from './subparts/DashboardSection';
import DashboardSkeleton from './subparts/DashboardSkeleton';
import DashboardSectionNav from './subparts/DashboardSectionNav';
import DashboardOverviewCharts from './subparts/DashboardOverviewCharts';
import {
    DASHBOARD_SECTIONS,
    buildStatsMap,
    getLatestStatTimestamp,
} from './dashboardStatsConfig';
import { getOverviewCharts } from './dashboardChartConfig';
import { resolveStatValue } from './dashboardStatUtils';
import './Dashboard.css';

function Dashboard() {
    const user = useEyeconxStore((state) => state.user);
    const userInfo = useEyeconxStore((state) => state.userInfo);
    const setTodaysAppointment = useEyeconxStore((state) => state.setTodaysAppointment);

    const [statsMap, setStatsMap] = useState({});
    const [statsRecords, setStatsRecords] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [loadError, setLoadError] = useState('');

    const getStat = useCallback((key) => resolveStatValue(statsMap, key), [statsMap]);

    const loadDashboard = useCallback(
        async (showRefreshToast = false) => {
            if (!user?.clinicId) {
                return;
            }

            if (showRefreshToast) {
                setIsRefreshing(true);
            } else {
                setIsLoading(true);
            }

            setLoadError('');

            try {
                const [statsResponse, appointmentsResponse] = await Promise.allSettled([
                    dashboardService.getDashboardStats(user.clinicId),
                    dashboardService.getAppointment(user.clinicId),
                ]);

                if (statsResponse.status === 'fulfilled') {
                    const records = statsResponse.value.data || [];
                    setStatsRecords(records);
                    setStatsMap(buildStatsMap(records));

                    if (showRefreshToast) {
                        toast.success('Dashboard refreshed');
                    }
                } else {
                    setStatsRecords([]);
                    setStatsMap({});
                    setLoadError('Dashboard stats are not available yet. They refresh on a scheduled job.');
                }

                if (appointmentsResponse.status === 'fulfilled') {
                    setTodaysAppointment(appointmentsResponse.value.data);
                }
            } catch (error) {
                console.error('Failed to load dashboard', error);
                toast.error('Failed to load dashboard');
            } finally {
                setIsLoading(false);
                setIsRefreshing(false);
            }
        },
        [setTodaysAppointment, user?.clinicId]
    );

    useEffect(() => {
        loadDashboard(false);
    }, [loadDashboard]);

    const overviewSection = DASHBOARD_SECTIONS[0];
    const detailSections = DASHBOARD_SECTIONS.slice(1);

    const totalStatCount = useMemo(
        () => DASHBOARD_SECTIONS.reduce((count, section) => count + section.stats.length, 0),
        []
    );

    const loadedStatCount = useMemo(
        () => Object.keys(statsMap).filter((key) => statsMap[key] !== '—').length,
        [statsMap]
    );

    const lastUpdated = useMemo(() => getLatestStatTimestamp(statsRecords), [statsRecords]);

    const overviewCharts = useMemo(() => getOverviewCharts(getStat), [getStat]);

    const clinicLabel = userInfo?.firstName
        ? `${userInfo.firstName}${userInfo.lastName ? ` ${userInfo.lastName}` : ''}`
        : 'Clinic overview';

    if (isLoading) {
        return <DashboardSkeleton />;
    }

    return (
        <div className="ecx-page dashboard-page ecx-page--full-width">
            <SubHeader
                title="Dashboard"
                description={`${clinicLabel} · ${totalStatCount} scheduled metrics across patients, communications, and operations`}
                icon="bi-speedometer2"
                containerClass="w-100 px-4"
            >
                <button
                    type="button"
                    className="btn btn-light-primary btn-sm rounded-pill"
                    onClick={() => loadDashboard(true)}
                    disabled={isRefreshing}
                >
                    <i className={`bi bi-arrow-clockwise me-1 ${isRefreshing ? 'ecx-refresh-spin' : ''}`} />
                    Refresh
                </button>
            </SubHeader>

            <div className="ecx-page-body">
                {loadError && (
                    <div className="dashboard-alert" role="alert">
                        <i className="bi bi-info-circle me-2" />
                        {loadError}
                    </div>
                )}

                <div className="dashboard-meta-bar">
                    <span className="dashboard-summary-chip dashboard-summary-chip--primary">
                        <i className="bi bi-bar-chart-line" />
                        {loadedStatCount} stats loaded
                    </span>
                    <span className="dashboard-summary-chip dashboard-summary-chip--purple">
                        <i className="bi bi-pie-chart" />
                        {overviewCharts.length} charts
                    </span>
                    <span className="dashboard-summary-chip dashboard-summary-chip--teal">
                        <i className="bi bi-layers" />
                        {DASHBOARD_SECTIONS.length} sections
                    </span>
                    {lastUpdated && (
                        <span className="dashboard-summary-chip dashboard-summary-chip--slate">
                            <i className="bi bi-clock-history" />
                            Updated {lastUpdated}
                        </span>
                    )}
                </div>

                <DashboardSectionNav sections={DASHBOARD_SECTIONS} />

                <DashboardOverviewCharts charts={overviewCharts} />

                <div className="dashboard-layout">
                    <div className="dashboard-layout__overview mb-3">
                        <DashboardSection section={overviewSection} getStat={getStat} />
                    </div>

                    {detailSections.map((section) => (
                        <DashboardSection key={section.id} section={section} getStat={getStat} />
                    ))}
                </div>
            </div>
        </div>
    );
}

export default Dashboard;
